import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building2,
  ShoppingCart,
  Package,
  FileText,
  AlertTriangle,
  Edit3,
  MessageSquare,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { cartService } from '../../services/cartService';
import { depotMessageService } from '../../services/depotMessageService';
import { productService } from '../../services/productService';
import { chantierService } from '../../services/chantierService';
import { inventoryService } from '../../services/inventoryService';
import { supplierService } from '../../services/supplierService';
import { retourService } from '../../services/retourService';
import { DepotMessage } from '../../types';
import DashboardCard from '../../components/dashboard/DashboardCard';

function Dashboard() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [depotMessage, setDepotMessage] = useState<DepotMessage | null>(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    alertProducts: 0,
    totalChantiers: 0,
    activeOrders: 0,
    totalSuppliers: 0,
    pendingInventories: 0,
    pendingRetours: 0
  });
  const [editingMessage, setEditingMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger toutes les données
      const [message, products, chantiers, orders, suppliers, inventories, retours] = await Promise.all([
        depotMessageService.getCurrentMessage(),
        productService.getProducts(),
        chantierService.getChantiers(),
        chantierService.getChantierOrders(),
        supplierService.getSuppliers(),
        inventoryService.getInventorySessions(),
        retourService.getRetours()
      ]);
      
      setDepotMessage(message);
      setMessageText(message?.message || '');
      
      // Calculer les statistiques
      const alertProducts = products.filter(p => productService.isStockAlert(p));
      const activeChantiers = chantiers.filter(c => c.status === 'en_cours');
      const activeOrders = orders.filter(o => ['panier', 'a_preparer', 'prete'].includes(o.status));
      const pendingInventories = inventories.filter(i => ['draft', 'in_progress'].includes(i.status));
      const pendingRetours = retours.filter(r => r.status === 'en_attente');
      
      setStats({
        totalProducts: products.length,
        alertProducts: alertProducts.length,
        totalChantiers: activeChantiers.length,
        activeOrders: activeOrders.length,
        totalSuppliers: suppliers.length,
        pendingInventories: pendingInventories.length,
        pendingRetours: pendingRetours.length
      });
      
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les données du tableau de bord',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMessage = async () => {
    if (!user) return;
    
    try {
      const updatedMessage = await depotMessageService.updateMessage(messageText, user.id);
      setDepotMessage(updatedMessage);
      setEditingMessage(false);
      
      addNotification({
        userId: user.id,
        title: 'Message mis à jour',
        message: 'Le message du dépôt a été mis à jour avec succès',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de mettre à jour le message',
        type: 'error'
      });
    }
  };

  const getRoleSpecificMessage = () => {
    switch (user?.role) {
      case 'responsable_bureau':
        return 'Vue d\'ensemble complète de l\'activité';
      case 'responsable_depot':
        return 'Gestion du dépôt';
      case 'responsable_equipe':
        return 'Gestion de votre équipe';
      case 'charpentier':
        return 'Votre espace de travail';
      case 'stagiaire':
        return 'Consultation des informations';
      default:
        return 'Bienvenue sur HERMINETTE';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Message du dépôt */}
      <div className="glass-card overflow-hidden rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg shadow-lg">
                <MessageSquare className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Message du dépôt</h3>
                {editingMessage ? (
                  <div className="space-y-3">
                    <textarea
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80 backdrop-blur-sm"
                      rows={3}
                      placeholder="Saisissez votre message..."
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveMessage}
                        className="btn-secondary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200"
                      >
                        Sauvegarder
                      </button>
                      <button
                        onClick={() => {
                          setEditingMessage(false);
                          setMessageText(depotMessage?.message || '');
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700 text-base leading-relaxed">
                    {depotMessage?.message || 'Aucun message du dépôt pour le moment.'}
                  </p>
                )}
              </div>
            </div>
            
            {(user?.role === 'responsable_depot' || user?.role === 'responsable_bureau') && !editingMessage && (
              <button
                onClick={() => setEditingMessage(true)}
                className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                title="Modifier le message"
              >
                <Edit3 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <DashboardCard
          title="Chantiers actifs"
          value={stats.totalChantiers}
          icon={Building2}
          color="green"
        />
        
        <DashboardCard
          title="Commandes en cours"
          value={stats.activeOrders}
          icon={ShoppingCart}
          color="orange"
        />
        
        <DashboardCard
          title="Produits"
          value={stats.totalProducts}
          icon={Package}
          color="green"
        />
        
        <DashboardCard
          title="Inventaires en cours"
          value={stats.pendingInventories}
          icon={FileText}
          color="amber"
        />
        
        <DashboardCard
          title="Alertes stock"
          value={stats.alertProducts}
          icon={AlertTriangle}
          color="red"
        />
        
        <DashboardCard
          title="Retours en attente"
          value={stats.pendingRetours}
          icon={RotateCcw}
          color="amber"
        />
      </div>

      {/* Recent Activity */}
      <div className="glass-card overflow-hidden rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <h3 className="text-xl leading-6 font-semibold text-gray-900 mb-6">
            Activité récente
          </h3>
          <div className="text-center py-8">
            <MessageSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune activité récente</h3>
            <p className="mt-1 text-sm text-gray-500">
              L'activité récente apparaîtra ici
            </p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-card overflow-hidden rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <h3 className="text-xl leading-6 font-semibold text-gray-900 mb-6">
            Actions rapides
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <button className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-300 text-left card-hover border border-purple-200/50">
              <div className="p-2 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg w-fit mb-3 shadow-lg">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">Gérer les chantiers</h4>
              <p className="text-xs text-gray-600 mt-1">Consulter et gérer les chantiers</p>
            </button>
            
            <button className="p-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl hover:from-indigo-100 hover:to-indigo-200 transition-all duration-300 text-left card-hover border border-indigo-200/50">
              <div className="p-2 bg-gradient-to-br from-indigo-400 to-indigo-600 rounded-lg w-fit mb-3 shadow-lg">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">Gérer les ventes</h4>
              <p className="text-xs text-gray-600 mt-1">Devis et commandes chantier</p>
            </button>
            
            {(user?.role === 'admin' || user?.role === 'responsable_bureau') && (
              <button className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-300 text-left card-hover border border-blue-200/50">
                <div className="p-2 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg w-fit mb-3 shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Gérer les utilisateurs</h4>
                <p className="text-xs text-gray-600 mt-1">Ajouter ou modifier des utilisateurs</p>
              </button>
            )}
            
            <button className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-300 text-left card-hover border border-green-200/50">
              <div className="p-2 bg-gradient-to-br from-green-400 to-green-600 rounded-lg w-fit mb-3 shadow-lg">
                <Package className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">Gérer les produits</h4>
              <p className="text-xs text-gray-600 mt-1">Consulter et gérer le stock</p>
            </button>
            
            <button 
              onClick={() => {
                // Ajouter quelques produits d'exemple au panier pour la démo
                cartService.addToCart('prod-1', 10);
                cartService.addToCart('prod-2', 5);
                addNotification({
                  userId: user?.id || '',
                  title: 'Produits ajoutés',
                  message: 'Produits d\'exemple ajoutés au panier',
                  type: 'success'
                });
              }}
              className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:from-orange-100 hover:to-orange-200 transition-all duration-300 text-left card-hover border border-orange-200/50"
            >
              <div className="p-2 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg w-fit mb-3 shadow-lg">
                <ShoppingCart className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900">Tester le panier</h4>
              <p className="text-xs text-gray-600 mt-1">Ajouter des produits d'exemple</p>
            </button>
            
            {(user?.role === 'responsable_depot' || user?.role === 'responsable_bureau') && (
              <button className="p-6 bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl hover:from-teal-100 hover:to-teal-200 transition-all duration-300 text-left card-hover border border-teal-200/50">
                <div className="p-2 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg w-fit mb-3 shadow-lg">
                  <Package className="h-6 w-6 text-white" />
                </div>
                <h4 className="text-sm font-semibold text-gray-900">Gérer les magasins</h4>
                <p className="text-xs text-gray-600 mt-1">Inventaires et activité stock</p>
              </button>
            )}
            
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;